import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

interface StepItem {
  title: string;
  description: string;
}

interface WorkflowStepperProps {
  steps: StepItem[];
}

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

export default function WorkflowStepper({ steps }: WorkflowStepperProps) {
  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: { xs: 3, md: 4 }, 
        borderRadius: 3, 
        bgcolor: 'white',
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: `repeat(${steps.length}, 1fr)` }, 
          gap: { xs: 4, md: 3 }, 
          position: 'relative' 
        }}
      >
        {steps.map((step, index) => (
          <Box 
            key={index} 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              textAlign: 'center',
              position: 'relative',
              zIndex: 2,
              flex: 1
            }}
          >
            {/* Connector Line (drawn behind the icons) */}
            {index < steps.length - 1 && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: '14px', // vertical center of the 28px icon
                  left: 'calc(50% + 14px)', 
                  right: 'calc(-50% + 14px)', 
                  height: '2px', 
                  bgcolor: '#e0e0e0', 
                  zIndex: 1,
                  display: { xs: 'none', md: 'block' }
                }} 
              />
            )}

            {/* Checkmark Icon */}
            <CheckCircle 
              sx={{ 
                color: BRAND_GREEN, 
                fontSize: 28, 
                zIndex: 3, 
                bgcolor: 'white', 
                borderRadius: '50%',
                mb: 2
              }} 
            />

            {/* Title */}
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 700, 
                color: BRAND_BLUE, 
                mb: 1.5,
                minHeight: { md: '2.5rem' }, // ensures titles align vertically even if they wrap to 2 lines
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1.3
              }}
            >
              {step.title}
            </Typography>

            {/* Description */}
            <Typography 
              sx={{ 
                color: '#666', 
                fontSize: '0.9rem', 
                lineHeight: 1.6,
                maxWidth: '220px',
                mx: 'auto'
              }}
            >
              {step.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
